/**
 * Search result cards.
 *
 * One card per entity type. Kept out of the search page so that the page stays
 * about *state* — filters, sorting, pagination — and these stay about
 * presentation. They are also reused outside search.
 */

import { Link } from 'react-router-dom'
import { VerificationBadge } from '@/components/data/trust'
import { BuildingIcon, MapPinIcon, PackageIcon } from '@/components/ui/icons'
import { Badge, cx } from '@/components/ui/primitives'
import { formatMoney, humanise } from '@/utils/format'
import type { BusinessSummary, ProductSummary, Service } from '@/types/api'

export function BusinessCard({ business }: { business: BusinessSummary }) {
  return (
    <Link
      to={business.canonical_path}
      className="group block panel p-4 transition-colors hover:border-[var(--accent-border)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-text)]">
              {business.name}
            </h3>
            <VerificationBadge status={business.verification_status} size="xs" />
            {business.is_claimed ? (
              <Badge tone="accent" size="xs">
                Claimed
              </Badge>
            ) : null}
          </div>

          {business.catalogue_summary || business.short_description ? (
            <p className="mt-1.5 line-clamp-2 text-sm text-[var(--text-secondary)]">
              {business.catalogue_summary || business.short_description}
            </p>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--text-tertiary)]">
            <span className="inline-flex items-center gap-1">
              <BuildingIcon size={12} />
              {humanise(business.business_type)}
            </span>
            {business.location ? (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon size={12} />
                {business.location}
              </span>
            ) : null}
            {business.website_domain ? <span>{business.website_domain}</span> : null}
            <span className="tabular">
              {business.source_count} source{business.source_count === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* The two scores travel together everywhere, including in a list row —
            either one alone invites the wrong conclusion. On phones the meters
            have no room, so the same pair appears as a compact line instead of
            disappearing: hiding the product's headline trust signal from the
            device most Nigerians browse on would be hiding the product. */}
        <span className="text-2xs tabular text-[var(--text-tertiary)] sm:hidden">
          Quality {business.data_quality_score} · Corroboration {business.trust_score}
        </span>
        <div className="hidden w-28 shrink-0 space-y-2 sm:block">
          <MiniScore label="Quality" value={business.data_quality_score} tone="var(--color-info)" />
          <MiniScore label="Corroboration" value={business.trust_score} tone="var(--accent)" />
        </div>
      </div>
    </Link>
  )
}

function MiniScore({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-2xs text-[var(--text-tertiary)]">{label}</span>
        <span className="text-2xs font-medium tabular" style={{ color: tone }}>
          {value}
        </span>
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(2, value)}%`, background: tone }}
        />
      </div>
    </div>
  )
}

export function ProductCard({ product }: { product: ProductSummary }) {
  return (
    <Link
      to={product.canonical_path}
      className="group block panel p-4 transition-colors hover:border-[var(--accent-border)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent-text)]">
            {product.name}
          </h3>
          {product.short_description ? (
            <p className="mt-1 line-clamp-1 text-sm text-[var(--text-secondary)]">
              {product.short_description}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--text-tertiary)]">
            <span className="inline-flex items-center gap-1">
              <PackageIcon size={12} />
              {product.supplier_count} supplier{product.supplier_count === 1 ? '' : 's'}
            </span>
            {product.unit ? <span>per {product.unit}</span> : null}
            {product.brand ? <span>{product.brand.name}</span> : null}
          </div>
        </div>

        <div className="shrink-0 text-right">
          {product.lowest_price ? (
            <>
              <p className="text-2xs text-[var(--text-tertiary)]">from</p>
              <p className="text-base font-semibold tabular text-[var(--text-primary)]">
                {formatMoney(product.lowest_price)}
              </p>
              {/* The spread is the reason to click through: it is what choosing
                  the right supplier is worth on this item. */}
              {product.price_spread_percent ? (
                <p className="mt-0.5 text-2xs tabular text-[var(--color-caution)]">
                  {product.price_spread_percent}% spread
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-[var(--text-tertiary)]">On request</p>
          )}
        </div>
      </div>
    </Link>
  )
}

export function ServiceCard({ service }: { service: Service }) {
  return (
    <div className={cx('panel p-4')}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-[var(--text-primary)]">
            {service.name}
          </h3>
          {service.short_description ? (
            <p className="mt-1 line-clamp-2 text-sm text-[var(--text-secondary)]">
              {service.short_description}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--text-tertiary)]">
            <span>{humanise(service.pricing_type)}</span>
            {service.service_area ? <span>{service.service_area}</span> : null}
            {service.is_remote ? (
              <Badge tone="info" size="xs">
                Remote
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {service.starting_price ? (
            <>
              <p className="text-2xs text-[var(--text-tertiary)]">from</p>
              <p className="text-base font-semibold tabular text-[var(--text-primary)]">
                {formatMoney(service.starting_price)}
              </p>
            </>
          ) : (
            <p className="text-sm text-[var(--text-tertiary)]">On request</p>
          )}
        </div>
      </div>
    </div>
  )
}
